import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import StageChangeModal from '../components/StageChangeModal';
import OwnershipChip from '../components/OwnershipChip';
import NextEventChip from '../components/NextEventChip';
import { useConsultantOptions } from '../lib/useDirectory';
import { quotesService, leadsService } from '../lib/services';
import { useCrm, isClosedStage } from '../lib/crm';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';
import { useVehicleMap } from '../lib/useVehicleMap';
import {
  DEAL_COLUMNS, isDealStage, toDeal, summarise, fmtMoney, fmtMoneyEst, dealStageLabel,
} from '../lib/deals';

/**
 * Deal pipeline — the commercial view of the Lead Accelerator.
 *
 * A deal is a lead that has reached a commercial stage (see lib/deals.js).
 * Dragging a card writes the real LeadStage through PATCH /v1/leads/{id}/stage,
 * so a move survives a refresh — the previous board moved cards in memory only.
 */

const DND_MIME = 'application/x-spectraiq-deal';

/** "5m ago" / "3d ago" / "1mo ago" — the same wording the lead board's cards use. */
function relTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const m = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export default function DealsPage() {
  const { leads, customers, loading, moveDeal } = useCrm();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [view, setView] = useState('kanban');
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [pendingLost, setPendingLost] = useState(null);

  const { vehiclesById } = useVehicleMap();

  const deals = useMemo(
    () => leads
      .filter(l => isDealStage(l.stage))
      .map(l => toDeal(
        l,
        customers.find(c => c.id === l.customerId),
        l.vehicleOfInterestId ? vehiclesById.get(l.vehicleOfInterestId) : null,
      )),
    [leads, customers, vehiclesById],
  );

  const grouped = useMemo(
    () => DEAL_COLUMNS.map(col => {
      const items = deals.filter(d => d.stage === col.key);
      return {
        ...col,
        items,
        value: items.reduce((sum, d) => sum + d.value, 0),
      };
    }),
    [deals],
  );

  const totals = useMemo(() => summarise(deals), [deals]);

  // ── Card footer data: who handles the deal, and what happens next ──────────
  // Same sources the lead board's cards use, so the two boards agree.
  const { options: consultantOptions } = useConsultantOptions();
  const consultantById = useMemo(
    () => new Map(consultantOptions.map(o => [o.id, o.primary || 'a consultant'])),
    [consultantOptions],
  );
  const initialsOf = (id) => {
    const name = consultantById.get(id);
    if (!name) return '··';
    return name.split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '··';
  };

  const [nextEvents, setNextEvents] = useState({});
  const [nextEventsLoaded, setNextEventsLoaded] = useState(false);
  // Keyed on the id list, not on `deals`: the CRM context re-polls every few
  // seconds and hands back a new array each time, which would re-request the
  // next events on every poll for no change.
  const openDealIdsKey = useMemo(
    () => deals.filter(d => !isClosedStage(d.stage)).map(d => d.id).filter(Boolean).join(','),
    [deals],
  );
  useEffect(() => {
    let cancelled = false;
    const ids = openDealIdsKey ? openDealIdsKey.split(',') : [];
    if (!ids.length) { setNextEvents({}); setNextEventsLoaded(true); return undefined; }
    leadsService.nextEvents(ids)
      .then((map) => { if (!cancelled) { setNextEvents(map || {}); setNextEventsLoaded(true); } })
      .catch(() => { if (!cancelled) setNextEventsLoaded(false); });
    return () => { cancelled = true; };
  }, [openDealIdsKey]);

  // Quote / sale status per lead, so a card can show whether a deal has been
  // quoted or sold without opening it.
  const [quoteByLead, setQuoteByLead] = useState(new Map());
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [qResp, sResp] = await Promise.all([
          quotesService.list({ size: 200 }).catch(() => null),
          quotesService.listSales({ size: 200 }).catch(() => null),
        ]);
        const quotes = qResp?.data?.content ?? qResp?.content ?? [];
        const sales  = sResp?.data?.content ?? sResp?.content ?? [];
        const rank = { DRAFT: 1, SENT: 2, ACCEPTED: 3 };
        const m = new Map();
        for (const q of quotes) {
          if (!q.leadId) continue;
          const cur = m.get(q.leadId);
          const r = rank[q.status] || 0;
          if (!cur || r > cur.r) m.set(q.leadId, { label: q.status === 'SENT' ? 'Quoted' : q.status.charAt(0) + q.status.slice(1).toLowerCase(), r, sold: false });
        }
        for (const s of sales) {
          if (s.leadId) m.set(s.leadId, { label: 'Sold', r: 99, sold: true });
        }
        if (alive) setQuoteByLead(m);
      } catch { /* non-critical */ }
    })();
    return () => { alive = false; };
  }, [deals.length]);

  /* ─── drag and drop ─── */
  const onDragStart = (e, dealId) => {
    e.dataTransfer.setData(DND_MIME, dealId);
    e.dataTransfer.setData('text/plain', dealId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(dealId);
  };

  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };

  const onColumnDragOver = (e, stage) => {
    if (!e.dataTransfer.types.includes(DND_MIME) && !e.dataTransfer.types.includes('text/plain')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const onColumnDragLeave = (e, stage) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverStage === stage) setDragOverStage(null);
  };

  const onColumnDrop = async (e, stage) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData(DND_MIME) || e.dataTransfer.getData('text/plain');
    setDragOverStage(null);
    setDraggingId(null);
    if (!dealId) return;

    const deal = deals.find(d => d.id === dealId);
    if (!deal || deal.stage === stage) return;

    const who = deal.customer?.name || deal.title;

    // Won / Lost are terminal on the backend — a further move is rejected.
    if (isClosedStage(deal.stage)) {
      toast(`${who} is ${dealStageLabel(deal.stage)} — reopen the lead to move it.`, { variant: 'warning' });
      return;
    }

    // Lost needs a reason code before the backend will take it.
    if (stage === 'LOST') {
      setPendingLost(deal);
      return;
    }

    try {
      await moveDeal(dealId, stage, {});
      toast(`${who} → ${dealStageLabel(stage)}`, { variant: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, `Couldn’t move ${who}`), { variant: 'danger' });
    }
  };

  // Keyboard fallback — drag-and-drop alone is inaccessible.
  const onCardKeyDown = (e, deal) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/deals/${deal.id}`);
    }
  };

  return (
    <>
      <TopBar
        crumbs={['Deals']}
        right={
          <>
            <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--bg-glass)', borderRadius: 999, border: '1px solid var(--border-color)' }}>
              {[['kanban', 'Board'], ['table', 'Table']].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className="btn small"
                  style={{
                    border: 'none',
                    background: view === v ? 'var(--accent-primary)' : 'transparent',
                    color: view === v ? '#fff' : 'var(--text-secondary)',
                    borderRadius: 999,
                    padding: '4px 12px',
                  }}
                >{label}</button>
              ))}
            </div>
            <Link to="/leads" className="btn small">Open lead board →</Link>
          </>
        }
      />

      <div className="app-content">
        {/* Forecast strip — unit/deal counts lead (the relevant lens for lead
            management); dollar figures are retained but de-emphasised below. */}
        <div className="grid-4" style={{ marginBottom: 10 }}>
          <Tile l="OPEN DEALS" v={`${totals.openCount}`} sub="quoting, negotiating, committed" c="var(--accent-primary)" />
          <Tile l="WON" v={`${totals.wonCount}`} sub="deals closed won" c="var(--success)" />
          <Tile l="LOST" v={`${totals.lostCount}`} sub="deals closed lost" c="var(--danger)" />
          <Tile
            l="WIN RATE"
            v={`${Math.round(totals.winRate * 100)}%`}
            sub={`${totals.wonCount + totals.lostCount} closed deals`}
            c={totals.winRate > 0.5 ? 'var(--success)' : 'var(--warning)'}
          />
        </div>
        {/* Dollar values, secondary. A real driveaway price where there is one; the
            default transaction value otherwise, so those totals carry a "~". */}
        <div className="mono text-xs text-muted" style={{ marginBottom: 10 }}>
          Open pipeline {fmtMoneyEst(totals.openValue, totals.openEstimatedCount > 0)}
          {' · '}weighted (probability-adjusted) {fmtMoneyEst(totals.weighted, totals.openEstimatedCount > 0)}
          {' · '}won {fmtMoneyEst(totals.wonValue, totals.wonEstimatedCount > 0)}
        </div>

        {totals.anyEstimated && (
          <div className="mono text-xs text-muted" style={{ marginBottom: 10 }}>
            ⓘ ~ = estimate. {totals.openEstimatedCount} of {totals.openCount} open deals and {totals.wonEstimatedCount} of {totals.wonCount} won
            deals have no driveaway price and use the default transaction value, so these totals are not confirmed revenue.
          </div>
        )}

        {/* Board */}
        {view === 'kanban' && (
          <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 0, overflowX: 'auto', paddingBottom: 4 }}>
            {grouped.map((col, ci) => (
              <div
                key={col.key}
                className={`kanban-col animate-in ${dragOverStage === col.key ? 'drop-target' : ''}`}
                style={{ animationDelay: `${ci * 0.05}s`, flex: '1 1 240px', minWidth: 240 }}
                onDragOver={(e) => onColumnDragOver(e, col.key)}
                onDragLeave={(e) => onColumnDragLeave(e, col.key)}
                onDrop={(e) => onColumnDrop(e, col.key)}
              >
                <div className="kanban-col-header">
                  <span className="mono text-xs" style={{ letterSpacing: 1, color: col.color }}>
                    {col.label.toUpperCase()}
                  </span>
                  <span className="mono text-xs text-muted">
                    {col.items.length}{col.items.length > 0 && ` · ${fmtMoneyEst(col.value, col.items.some(d => d.valueIsEstimated))}`}
                  </span>
                </div>

                {col.items.length === 0 ? (
                  <div className={`kanban-empty mono text-xs text-muted ${dragOverStage === col.key ? 'drop-target' : ''}`}>
                    {dragOverStage === col.key
                      ? (col.key === 'LOST' ? 'Drop to mark lost' : 'Drop to move here')
                      : 'No deals currently in this stage'}
                  </div>
                ) : col.items.map(d => (
                  <div
                    key={d.id}
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => onDragStart(e, d.id)}
                    onDragEnd={onDragEnd}
                    onClick={() => navigate(`/deals/${d.id}`)}
                    onKeyDown={(e) => onCardKeyDown(e, d)}
                    className={`kanban-card ${draggingId === d.id ? 'dragging' : ''}`}
                    style={{ borderLeft: `3px solid ${col.color}` }}
                    title="Drag to move stage · click to open"
                  >
                    <div className="flex-between">
                      <span className="kc-name" style={{ fontWeight: 600 }}>
                        {d.customer?.name || 'Unknown customer'}
                      </span>
                      <span
                        className="mono text-xs"
                        style={{
                          color: d.score >= 80 ? 'var(--success)' : d.score <= 40 ? 'var(--danger)' : 'var(--warning)',
                          fontWeight: 700,
                        }}
                        title={`Lead score ${d.score}/100 — how engaged and ready to buy the customer looks. This is not the chance of winning the deal; that is the stage probability below.`}
                      >{d.score}</span>
                    </div>

                    <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>{d.title}</div>

                    <div className="flex-between" style={{ marginTop: 8 }}>
                      <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span className="pill">{d.source}</span>
                        {quoteByLead.get(d.id) && (
                          <span
                            className={`pill ${quoteByLead.get(d.id).sold ? 'success' : 'accent'} filled`}
                            style={{ fontSize: 10 }}
                            title="Quote / sale status"
                          >{quoteByLead.get(d.id).label}</span>
                        )}
                      </span>
                      <span
                        className="mono text-xs"
                        style={{ color: 'var(--success)', fontWeight: 600 }}
                        title={d.valueIsEstimated ? 'Estimated — no driveaway price on the lead' : 'Driveaway price'}
                      >
                        {d.valueIsEstimated ? '~' : ''}{fmtMoney(d.value)}
                      </span>
                    </div>

                    {/* Probability bar — how close this is to landing. */}
                    {d.probability > 0 && d.probability < 100 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ height: 3, background: 'var(--border-color)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${d.probability}%`, height: '100%', background: col.color }} />
                        </div>
                        <div
                          className="mono text-xs text-muted"
                          style={{ marginTop: 3 }}
                          title={`Win probability is set by the deal stage (${d.probability}% at this stage), not by the lead score. Weighted = value × probability.`}
                        >
                          {d.probability}% win chance · {fmtMoney(d.weighted)} weighted
                        </div>
                      </div>
                    )}

                    {/* Next step — or the warning that nothing is planned.
                        Same chip as the lead board; not shown once a deal is closed. */}
                    {nextEventsLoaded && !isClosedStage(d.stage) && (
                      <div style={{ margin: '8px 0 0', fontSize: 11 }}>
                        <NextEventChip nextEvent={nextEvents[d.id] || null} compact />
                      </div>
                    )}

                    {/* Who is handling it — the same row the lead board's cards carry. */}
                    <div className="lc-meta">
                      {d.assignedConsultantId ? (
                        <span
                          title={`Assigned to ${consultantById.get(d.assignedConsultantId) || 'a consultant'}`}
                          style={{
                            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                            background: 'var(--accent-primary)', color: '#fff', fontSize: 9, fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >{initialsOf(d.assignedConsultantId)}</span>
                      ) : (
                        <span
                          title="Unassigned — open the lead to assign a consultant"
                          style={{
                            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                            border: '1px dashed var(--warning)', color: 'var(--warning)', fontSize: 13, lineHeight: 1,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >+</span>
                      )}
                      {/* The consultant's name in words, not just initials. */}
                      {d.assignedConsultantId ? (
                        <span className="lc-chip" title="Assigned sales consultant">
                          {(consultantById.get(d.assignedConsultantId) || '…').split(' ')[0]}
                        </span>
                      ) : (
                        <span className="lc-chip" title="No consultant assigned yet" style={{ color: 'var(--warning)' }}>Unassigned</span>
                      )}
                      <OwnershipChip lead={d} compact />
                      {d.customer?.preferences?.channel && (
                        <span className="lc-chip" title={`Prefers ${d.customer.preferences.channel}`}>
                          {d.customer.preferences.channel}
                        </span>
                      )}
                      <span className="lc-time" title="When the lead was created">{relTime(d.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        {view === 'table' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>{['Customer', 'Vehicle', 'Stage', 'Score', 'Value', 'Prob.', 'Weighted', 'Source', ''].map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {deals.length === 0 ? (
                    <tr><td colSpan={9}>
                      <div className="empty-state" style={{ padding: 32 }}>
                        {!loading && <div className="icon">💰</div>}
                        <div className="title">{loading ? 'Loading deals…' : 'No deals yet'}</div>
                        <div className="desc">
                          A lead becomes a deal once it reaches Quoting. Work leads up the
                          board and they'll appear here.
                        </div>
                        {!loading && (
                          <Link to="/leads" className="btn small primary" style={{ marginTop: 8 }}>
                            Go to leads
                          </Link>
                        )}
                      </div>
                    </td></tr>
                  ) : deals.map(d => (
                    <tr key={d.id}>
                      <td>{d.customer?.name || '—'}</td>
                      <td className="mono text-xs">{d.title}</td>
                      <td>
                        <span className={`pill ${d.stage === 'DELIVERED' ? 'success' : d.stage === 'LOST' ? 'danger' : 'accent'} filled`}>
                          {dealStageLabel(d.stage)}
                        </span>
                      </td>
                      <td
                        className="mono text-xs"
                        title="Lead score (engagement), not win probability"
                        style={{ fontWeight: 700, color: d.score >= 80 ? 'var(--success)' : d.score <= 40 ? 'var(--danger)' : 'var(--warning)' }}
                      >
                        {d.score}
                      </td>
                      <td className="mono text-xs" style={{ color: 'var(--success)' }}>
                        {d.valueIsEstimated ? '~' : ''}{fmtMoney(d.value)}
                      </td>
                      <td className="mono text-xs text-muted" title="Win probability from the deal stage">{d.probability}%</td>
                      <td className="mono text-xs">{fmtMoney(d.weighted)}</td>
                      <td className="mono text-xs text-muted">{d.source}</td>
                      <td>
                        <Link to={`/deals/${d.id}`} className="mono text-xs" style={{ color: 'var(--accent-primary-hover)' }}>
                          Open →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <StageChangeModal
        open={!!pendingLost}
        lead={pendingLost}
        initialTarget="LOST"
        onClose={() => setPendingLost(null)}
        onChange={async (to, opts) => {
          const deal = pendingLost;
          await moveDeal(deal.id, to, opts);
          toast(`${deal.customer?.name || deal.title} → ${dealStageLabel(to)}`, {
            variant: to === 'LOST' ? 'warning' : 'success',
          });
        }}
      />
    </>
  );
}

function Tile({ l, v, sub, c }) {
  return (
    <div className="card">
      <div className="card-label">{l}</div>
      <div className="metric-value" style={{ fontSize: 26, color: c }}>{v}</div>
      <div className="mono text-xs text-muted">{sub}</div>
    </div>
  );
}
